import { Bot } from "lucide-react";
import { useLocation } from "react-router-dom";

interface QuickRepliesProps {
  lang: "sl" | "en";
  onSelect: (question: string) => void;
}

const DEFAULT_QUESTIONS = {
  sl: [
    "Kaj je uporovno varjenje?",
    "Vaši izdelki",
    "MFDC tehnologija",
    "Kontaktni podatki",
  ],
  en: [
    "What is resistance welding?",
    "Your products",
    "MFDC technology",
    "Contact info",
  ],
};

type ContextKey = "spotWelding" | "seamWelding" | "projectionWelding" | "buttWelding" | "microWelding" | "carSpotters" | "batteryProcessing" | "weldingHeads" | "weldingPincers" | "workstation" | "solutions" | "spareParts" | "consumables" | "services" | "aboutUs" | "knowledgeHub" | "faq" | "partners" | "references";

const CONTEXTUAL_QUESTIONS: Record<ContextKey, { sl: string[]; en: string[] }> = {
  spotWelding: {
    sl: ["Kateri točkovni varilnik je primeren zame?", "Razlika med AC in MFDC?", "Kakšne materiale lahko varim?", "Pošljite mi ponudbo"],
    en: ["Which spot welder suits my needs?", "Difference between AC and MFDC?", "What materials can I weld?", "Send me a quote"],
  },
  seamWelding: {
    sl: ["Kakšne šivne varilnike ponujate?", "Ali varite pillow plate plošče?", "Kakšna je max. debelina materiala?", "Kontaktni podatki"],
    en: ["What seam welders do you offer?", "Do you weld pillow plates?", "What's the max material thickness?", "Contact info"],
  },
  projectionWelding: {
    sl: ["Kakšne projekcijske varilnike imate?", "Za katere materiale je projekcijsko varjenje?", "Kakšna je produktivnost?", "Pošljite ponudbo"],
    en: ["What projection welders do you have?", "What materials suit projection welding?", "What's the productivity rate?", "Send me a quote"],
  },
  buttWelding: {
    sl: ["Kako deluje soležno varjenje?", "Za katere aplikacije je primerno?", "Specifikacije RP-97?", "Kontakt"],
    en: ["How does butt welding work?", "What applications is it suitable for?", "RP-97 specifications?", "Contact"],
  },
  microWelding: {
    sl: ["Kakšne mikro varilnike ponujate?", "Za katere materiale je mikro varjenje?", "Minimalna debelina materiala?", "Kontakt"],
    en: ["What micro welders do you offer?", "What materials suit micro welding?", "Minimum material thickness?", "Contact"],
  },
  carSpotters: {
    sl: ["Kateri car spotter je najboljši?", "Ali imate MFDC car spotter?", "Primerjava Car Point in Europei?", "Ponudba"],
    en: ["Which car spotter is best?", "Do you have MFDC car spotters?", "Compare Car Point vs Europei?", "Get a quote"],
  },
  batteryProcessing: {
    sl: ["Varjenje baterij — kakšne rešitve?", "Kakšne materiale varite pri baterijah?", "Produktivnost pri varjenju baterij?", "Ponudba"],
    en: ["Battery welding — what solutions?", "What materials for battery welding?", "Productivity for battery welding?", "Get a quote"],
  },
  weldingHeads: {
    sl: ["Kakšne varilne glave ponujate?", "Razlika med DK in SK serijama?", "Katera glava za mojo aplikacijo?", "Kontakt"],
    en: ["What welding heads do you offer?", "Difference between DK and SK series?", "Which head for my application?", "Contact"],
  },
  weldingPincers: {
    sl: ["Kakšne varilne klešče imate?", "Za katere aplikacije so klešče?", "Specifikacije SZ serije?", "Ponudba"],
    en: ["What welding pincers do you have?", "What applications use pincers?", "SZ series specifications?", "Get a quote"],
  },
  workstation: {
    sl: ["Kakšne varilne postaje ponujate?", "Ročna vs. polavtomatska postaja?", "Prilagoditve po meri?", "Kontakt"],
    en: ["What workstations do you offer?", "Manual vs semi-automatic station?", "Custom modifications?", "Contact"],
  },
  solutions: {
    sl: ["Kakšne rešitve ponujate?", "Ali imate rešitve za avtomobilsko industrijo?", "Rešitve za kuhinjsko opremo?", "Kontakt"],
    en: ["What solutions do you offer?", "Solutions for automotive industry?", "Solutions for kitchen equipment?", "Contact"],
  },
  spareParts: {
    sl: ["Kateri rezervni deli so na voljo?", "Ali imate Bosch Rexroth dele?", "Dobavni rok za rezervne dele?", "Naročilo"],
    en: ["Which spare parts are available?", "Do you carry Bosch Rexroth parts?", "Lead time for spare parts?", "Order"],
  },
  consumables: {
    sl: ["Kakšne elektrode ponujate?", "Katera elektroda za moj material?", "Koliko zdržijo elektrode?", "Naročilo"],
    en: ["What electrodes do you offer?", "Which electrode for my material?", "How long do electrodes last?", "Order"],
  },
  services: {
    sl: ["Kakšne storitve ponujate?", "Ali izvajate varilne teste?", "Ali nudite šolanja?", "Kontakt"],
    en: ["What services do you offer?", "Do you perform welding tests?", "Do you offer training?", "Contact"],
  },
  aboutUs: {
    sl: ["Koliko izkušenj imate?", "Kje se nahajate?", "Kakšne certifikate imate?", "Kontakt"],
    en: ["How much experience do you have?", "Where are you located?", "What certifications do you hold?", "Contact"],
  },
  knowledgeHub: {
    sl: ["Kaj je uporovno varjenje?", "Razlika med AC in MFDC?", "Kakšni so tipi uporovnega varjenja?", "Kontakt"],
    en: ["What is resistance welding?", "Difference between AC and MFDC?", "Types of resistance welding?", "Contact"],
  },
  faq: {
    sl: ["Kako izbrati pravi varilnik?", "Kakšni materiali se varijo?", "Koliko stane varilni stroj?", "Kontakt"],
    en: ["How to choose the right welder?", "What materials can be welded?", "How much does a welding machine cost?", "Contact"],
  },
  partners: {
    sl: ["Kdo so vaši partnerji?", "Ali ste Bosch Rexroth partner?", "Ali sodelujete z Harms & Wende?", "Kontakt"],
    en: ["Who are your partners?", "Are you a Bosch Rexroth partner?", "Do you work with Harms & Wende?", "Contact"],
  },
  references: {
    sl: ["Kdo so vaše stranke?", "Ali delate za avtomobilsko industrijo?", "Koliko strojev ste dostavili?", "Kontakt"],
    en: ["Who are your clients?", "Do you serve the automotive industry?", "How many machines delivered?", "Contact"],
  },
};

/** Map pathname patterns to context keys */
function getContextKey(pathname: string): ContextKey | null {
  const p = pathname.toLowerCase();
  if (p.includes("spot-welding") && !p.includes("robotic") && !p.includes("sinks")) return "spotWelding";
  if (p.includes("car-spotter")) return "carSpotters";
  if (p.includes("seam-weld") || p.includes("pillow")) return "seamWelding";
  if (p.includes("projection-weld")) return "projectionWelding";
  if (p.includes("butt-weld")) return "buttWelding";
  if (p.includes("micro-weld")) return "microWelding";
  if (p.includes("battery")) return "batteryProcessing";
  if (p.includes("welding-head")) return "weldingHeads";
  if (p.includes("welding-pincer")) return "weldingPincers";
  if (p.includes("workstation")) return "workstation";
  if (p.includes("/solutions")) return "solutions";
  if (p.includes("spare-part")) return "spareParts";
  if (p.includes("consumable")) return "consumables";
  if (p.includes("service")) return "services";
  if (p.includes("about")) return "aboutUs";
  if (p.includes("knowledge")) return "knowledgeHub";
  if (p.includes("faq")) return "faq";
  if (p.includes("partner")) return "partners";
  if (p.includes("users-clients") || p.includes("testimonial") || p.includes("references")) return "references";
  return null;
}

const QuickReplies = ({ lang, onSelect }: QuickRepliesProps) => {
  const { pathname } = useLocation();
  const contextKey = getContextKey(pathname);
  const questions = contextKey
    ? CONTEXTUAL_QUESTIONS[contextKey][lang]
    : DEFAULT_QUESTIONS[lang];

  return (
    <div className="text-center text-muted-foreground text-sm py-6 px-2">
      <Bot className="w-10 h-10 mx-auto mb-3 text-primary/40" />
      <p className="font-medium text-foreground mb-1">
        {lang === "sl" ? "Pozdravljeni! 👋" : "Hello! 👋"}
      </p>
      <p className="mb-4">
        {lang === "sl"
          ? "Sem AI pomočnik podjetja Kočevar. Vprašajte me karkoli o uporovnem varjenju ali naših izdelkih."
          : "I'm Kočevar's AI assistant. Ask me anything about resistance welding or our products."}
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        {questions.map((q) => (
          <button
            key={q}
            onClick={() => onSelect(q)}
            className="text-xs px-3 py-1.5 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
};

export default QuickReplies;
